import { Command } from 'cmdk';
import { Bot, Box, ClipboardList, FileCode2, Files, FlaskConical, Route, Waypoints, ClipboardPaste, Columns2, CornerDownRight, Crosshair, Download, FilePlus2, FileText, FolderX, GitBranchPlus, GitCommitVertical, History, Layers, LayoutGrid, Map, MonitorDown, Moon, MoveHorizontal, Network, PenLine, Rows3, Spline, Sun, Table2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { CascadeIcon } from '../diagram/DeletePanel';
import { LAYOUTS, SPACINGS } from '../diagram/layouts';
import { useDiagramSettings } from '../diagram/settings';
import { useTheme } from '../lib/theme';
import { openCopyForAgent } from '../agent/CopyForAgent';
import { exportAllSketches } from '../sketch/files';
import { openPasteDialog } from '../sketch/PasteDialog';
import { closeAllPanels, MODEL_TABS, openCodeMap, openCompare, openEndpointMap, openDiagram, openFiles, openGraph, openNote, openSketch, openTestMap, openTimeline, tabKind, useActiveTab } from '../state/dock';
import { commitPaths, selectFile, showHistory, useFiles } from '../state/files';
import { mappedFiles, revealCode, showEndpoint, showTest, useCodeMap } from '../state/codemap';
import { useCodeSettings } from '../code/settings';
import { typeName } from '../code/testGraph';
import { installApp, usePwa } from '../state/pwa';
import { baseRefOf } from '../state/sketches';
import { closeSource, refKey, revealEntity, useWorkspace } from '../state/workspace';
import { branchFromActive, migrationDate, newNote } from './Explorer';

export const openPalette = { current: () => {} };

/**
 * The files to jump to while the Files tab is in front: the selected commit's changes, and once
 * something is typed, every file that matches (of the commit, or of a folder without git).
 */
function useFileList(active: boolean, query: string): string[] {
  const changes = useFiles((s) => s.changes);
  const commit = useFiles((s) => s.commit);
  const folderFiles = useFiles((s) => s.folderFiles);
  const git = useFiles((s) => s.repo.state === 'ready');
  const [all, setAll] = useState<{ commit: string; paths: string[] } | null>(null);
  useEffect(() => {
    if (!active || !git || !commit || all?.commit === commit) return;
    let cancelled = false;
    void commitPaths(commit).then((paths) => !cancelled && setAll({ commit, paths }));
    return () => {
      cancelled = true;
    };
  }, [active, git, commit, all]);
  return useMemo(() => {
    if (!active) return [];
    const q = query.trim().toLowerCase();
    const changed = (changes ?? []).map((c) => c.path);
    if (!q) return changed;
    const paths = git ? (all?.commit === commit ? all.paths : changed) : (folderFiles ?? []);
    return paths.filter((p) => p.toLowerCase().includes(q)).slice(0, 200);
  }, [active, query, changes, git, all, commit, folderFiles]);
}

function Item({ onSelect, icon, children, hint, value }: { onSelect: () => void; icon: React.ReactNode; children: React.ReactNode; hint?: string; value: string }) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex h-8 cursor-default items-center gap-2.5 rounded-md px-2.5 text-[13px] text-muted data-[selected=true]:bg-active data-[selected=true]:text-text pointer-coarse:h-10"
    >
      <span className="flex-none text-faint">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint && <span className="flex-none text-[11.5px] text-faint">{hint}</span>}
    </Command.Item>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group heading={heading} className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-faint [&_[cmdk-group-heading]]:uppercase">
      {children}
    </Command.Group>
  );
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const discovery = useWorkspace((s) => s.discovery);
  const activeRef = useWorkspace((s) => s.activeRef);
  const model = useWorkspace((s) => (s.activeRef ? s.models[refKey(s.activeRef)] : undefined));
  const sketches = useWorkspace((s) => s.sketches);
  const notes = useWorkspace((s) => s.notes);
  const activeSketch = activeRef?.sketchId ? sketches[activeRef.sketchId] : undefined;
  // What "compare" means for the model in view: a sketch with its base, a migration with the current model.
  const compareWith = activeSketch ? baseRefOf(activeSketch) : activeRef?.migrationId ? { setKey: activeRef.setKey, migrationId: null } : null;
  const theme = useTheme();
  const settings = useDiagramSettings();
  const canInstall = usePwa((s) => s.install !== null);
  const codeMap = useCodeMap((s) => (s.view === 'code' ? s.map : null));
  const endpointMap = useCodeMap((s) => (s.view === 'endpoints' ? s.map : null));
  const testMap = useCodeMap((s) => (s.view === 'tests' ? s.map : null));
  const hideTests = useCodeSettings((s) => s.tests === 'hide');
  const hasCode = useWorkspace((s) => s.files.length > 0) && open && mappedFiles().length > 0;
  // What the tab in front shows: entities and diagram settings only where there is a model or a diagram.
  const tab = useActiveTab((s) => tabKind(s.subject));
  const modelInFront = MODEL_TABS.has(tab ?? '');
  const diagramInFront = modelInFront || tab === 'codemap' || tab === 'endpoints' || tab === 'tests';
  const filesInFront = tab === 'files';
  const fileList = useFileList(open && filesInFront, query);
  const history = useFiles((s) => s.history);
  const shownFile = useFiles((s) => (filesInFront && s.repo.state === 'ready' ? s.file : null));

  useEffect(() => {
    openPalette.current = () => setOpen(true);
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'p')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const run = (action: () => void) => {
    setOpen(false);
    setQuery('');
    action();
  };
  const selection = useWorkspace((s) => s.selection);
  const selectedEntity = activeRef && selection && refKey(selection.ref) === refKey(activeRef) ? (selection.value.kind === 'entity' ? selection.value.key.split('.')[0]! : selection.value.kind === 'property' ? selection.value.entity : null) : null;

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
      }}
      label="Command palette"
      overlayClassName="fixed inset-0 z-50 bg-black/30"
      // On a phone it sits at the top, so the list stays above the on-screen keyboard.
      contentClassName="fixed top-[14vh] left-1/2 z-50 w-[min(620px,92vw)] -translate-x-1/2 overflow-hidden rounded-xl border border-border-strong bg-panel-2 shadow-[var(--shadow-pop)] max-md:top-3 max-md:w-[calc(100vw-1.5rem)]"
    >
      <Command.Input
        value={query}
        onValueChange={setQuery}
        placeholder="Jump to an entity, migration or action…"
        className="h-12 w-full border-b border-border bg-transparent px-4 text-[14px] outline-none placeholder:text-faint"
      />
      <Command.List className="max-h-[52vh] overflow-y-auto overscroll-contain p-1.5 max-md:max-h-[42vh]">
        <Command.Empty className="px-3 py-6 text-center text-muted">Nothing matches.</Command.Empty>
        {codeMap && (
          <Group heading="Code">
            {codeMap.files
              .filter((f) => f.types.length && !(hideTests && f.test))
              .map((f) => (
                <Item key={f.path} value={`file ${f.path}`} icon={<FileCode2 size={14} />} hint={f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : undefined} onSelect={() => run(() => revealCode(f.path))}>
                  {f.path.slice(f.path.lastIndexOf('/') + 1)}
                </Item>
              ))}
            {query.trim() &&
              codeMap.types.map((t) => (
                <Item key={t.id} value={`type ${t.kind} ${t.id}`} icon={<Box size={14} />} hint={t.kind} onSelect={() => run(() => t.parts[0] && revealCode(t.parts[0].file))}>
                  {t.name}
                </Item>
              ))}
          </Group>
        )}
        {endpointMap && (
          <Group heading="Endpoints">
            {endpointMap.endpoints.map((e) => (
              <Item key={e.id} value={`endpoint ${e.id}`} icon={<Route size={14} />} hint={e.kind === 'controller' ? 'controller' : 'minimal API'} onSelect={() => run(() => showEndpoint(e.id))}>
                {e.id}
              </Item>
            ))}
          </Group>
        )}
        {testMap && (
          <Group heading="Tests">
            {testMap.tests.map((t) => (
              <Item key={t.id} value={`test ${t.id}`} icon={<FlaskConical size={14} />} hint={typeName(t.type)} onSelect={() => run(() => showTest(t.id))}>
                {t.name}
              </Item>
            ))}
          </Group>
        )}
        {fileList.length > 0 && (
          <Group heading="Files">
            {fileList.map((path) => (
              <Item key={path} value={`file ${path}`} icon={<FileText size={14} />} hint={path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : undefined} onSelect={() => run(() => selectFile(path))}>
                {path.slice(path.lastIndexOf('/') + 1)}
              </Item>
            ))}
          </Group>
        )}
        {activeRef && model && modelInFront && (
          <Group heading="Entities">
            {Object.entries(model.result.doc.model.entities).map(([key, e]) => (
              <Item key={key} value={`entity ${key} ${e.clrName ?? ''}`} icon={<Table2 size={14} />} hint={e.mapping?.table ?? undefined} onSelect={() => run(() => revealEntity(activeRef, key))}>
                {key}
              </Item>
            ))}
          </Group>
        )}
        {activeRef && model && modelInFront && query.trim() && (
          <Group heading="Focus">
            {Object.keys(model.result.doc.model.entities).map((key) => (
              <Item key={key} value={`focus on ${key}`} icon={<Crosshair size={14} />} hint="it and what it relates to" onSelect={() => run(() => revealEntity(activeRef, key, { focus: true }))}>
                Focus on {key}
              </Item>
            ))}
            {Object.keys(model.result.doc.model.entities).map((key) => (
              <Item key={`delete ${key}`} value={`delete ${key} cascade`} icon={<CascadeIcon size={14} />} hint="what the database does" onSelect={() => run(() => revealEntity(activeRef, key, { deletion: true }))}>
                What deleting a {key} does
              </Item>
            ))}
          </Group>
        )}
        {diagramInFront && query.trim() && (
          <Group heading="Layout">
            {LAYOUTS.map((l) => (
              <Item key={l.id} value={`${l.label} layout arrange`} icon={<LayoutGrid size={14} />} hint={settings.layout === l.id ? 'current' : l.description} onSelect={() => run(() => settings.setLayout(l.id))}>
                Arrange: {l.label}
              </Item>
            ))}
            {SPACINGS.map((s) => (
              <Item key={s.id} value={`${s.label} spacing gaps room`} icon={<MoveHorizontal size={14} />} hint={settings.spacing === s.id ? 'current' : s.description} onSelect={() => run(() => settings.setSpacing(s.id))}>
                Spacing: {s.label.toLowerCase()}
              </Item>
            ))}
            <Item value="Right angles lines square edges" icon={<CornerDownRight size={14} />} hint={settings.edgeStyle === 'square' ? 'current' : undefined} onSelect={() => run(() => settings.setEdgeStyle('square'))}>
              Lines: right angles
            </Item>
            <Item value="Smooth curves lines curved edges" icon={<Spline size={14} />} hint={settings.edgeStyle === 'smooth' ? 'current' : undefined} onSelect={() => run(() => settings.setEdgeStyle('smooth'))}>
              Lines: smooth curves
            </Item>
          </Group>
        )}
        {discovery && (
          <Group heading="Models">
            {discovery.sets.map((set) => (
              <Item key={set.key} value={`current model ${set.contextName}`} icon={<Layers size={14} />} hint="current" onSelect={() => run(() => openDiagram({ setKey: set.key, migrationId: null }))}>
                {set.contextName}
              </Item>
            ))}
            {discovery.sets.map((set) => (
              <Item key={`${set.key}|history`} value={`history timeline ${set.contextName}`} icon={<History size={14} />} hint="history" onSelect={() => run(() => openTimeline(set.key))}>
                {set.contextName}
              </Item>
            ))}
            {discovery.sets.flatMap((set) =>
              [...set.migrations].reverse().map((m) => (
                <Item
                  key={`${set.key}|${m.id}`}
                  value={`migration ${m.id} ${set.contextName}`}
                  icon={<GitCommitVertical size={14} />}
                  hint={`${set.contextName} · ${migrationDate(m.timestamp)}`}
                  onSelect={() => run(() => openTimeline(set.key, m.id))}
                >
                  {m.name}
                </Item>
              )),
            )}
          </Group>
        )}
        {Object.keys(sketches).length > 0 && (
          <Group heading="Sketches">
            {Object.values(sketches)
              .sort((a, b) => b.updated - a.updated)
              .map((sk) => (
                <Item key={sk.id} value={`sketch ${sk.doc.title}`} icon={<PenLine size={14} />} hint="sketch" onSelect={() => run(() => openSketch(sk.id))}>
                  {sk.doc.title}
                </Item>
              ))}
          </Group>
        )}
        {Object.keys(notes).length > 0 && (
          <Group heading="Notes">
            {Object.values(notes)
              .sort((a, b) => b.updated - a.updated)
              .map((n) => (
                <Item key={n.id} value={`note ${n.title}`} icon={<FileText size={14} />} hint="note" onSelect={() => run(() => openNote(n.id))}>
                  {n.title}
                </Item>
              ))}
          </Group>
        )}
        <Group heading="Actions">
          {shownFile && history !== shownFile && (
            <Item value={`History of this file commits that changed ${shownFile}`} icon={<History size={14} />} onSelect={() => run(() => void showHistory(shownFile))}>
              Show the commits that changed {shownFile.slice(shownFile.lastIndexOf('/') + 1)}
            </Item>
          )}
          {filesInFront && history && (
            <Item value="Show every commit again history" icon={<History size={14} />} onSelect={() => run(() => void showHistory(null))}>
              Show every commit again
            </Item>
          )}
          <Item value="Open the files git history commits changes diff" icon={<Files size={14} />} onSelect={() => run(openFiles)}>
            Open the files
          </Item>
          {hasCode && (
            <Item value="Open the code map calls files members code" icon={<Waypoints size={14} />} onSelect={() => run(openCodeMap)}>
              Open the code map
            </Item>
          )}
          {hasCode && (
            <Item value="Open the endpoint map endpoints routes http api controllers" icon={<Route size={14} />} onSelect={() => run(openEndpointMap)}>
              Open the endpoint map
            </Item>
          )}
          {hasCode && (
            <Item value="Open the test map tests unit xunit nunit mstest mocks" icon={<FlaskConical size={14} />} onSelect={() => run(openTestMap)}>
              Open the test map
            </Item>
          )}
          {discovery && (
            <Item value="Copy for agent prompt claude code design" icon={<Bot size={14} />} onSelect={() => run(() => openCopyForAgent())}>
              Copy for agent
            </Item>
          )}
          <Item
            value="Download the agent kit skill"
            icon={<Download size={14} />}
            onSelect={() =>
              run(() => {
                const a = document.createElement('a');
                a.href = '/agent-kit.zip';
                a.download = 'modelviewer-agent-kit.zip';
                a.click();
              })
            }
          >
            Download the agent kit
          </Item>
          {discovery && (
            <Item value="New note" icon={<FilePlus2 size={14} />} onSelect={() => run(() => void newNote())}>
              New note
            </Item>
          )}
          {discovery && (
            <Item value="New use case note decide design" icon={<ClipboardList size={14} />} onSelect={() => run(() => void newNote('use case'))}>
              New use case
            </Item>
          )}
          {discovery && (
            <Item value="Open the graph of notes and links" icon={<Network size={14} />} onSelect={() => run(openGraph)}>
              Open the graph
            </Item>
          )}
          {discovery && (
            <Item value="New sketch from the model in view branch" icon={<GitBranchPlus size={14} />} onSelect={() => run(() => void branchFromActive())}>
              New sketch from the model in view
            </Item>
          )}
          {activeRef && compareWith && modelInFront && (
            <Item value="Compare with the model it started from current model" icon={<Columns2 size={14} />} onSelect={() => run(() => openCompare([compareWith, activeRef]))}>
              Compare with {activeSketch ? 'the model it started from' : 'the current model'}
            </Item>
          )}
          {discovery && (
            <Item value="Paste a sketch clipboard import" icon={<ClipboardPaste size={14} />} onSelect={() => run(openPasteDialog)}>
              Paste a sketch
            </Item>
          )}
          {Object.keys(sketches).length > 0 && (
            <Item value="Download all sketches export zip" icon={<Download size={14} />} onSelect={() => run(() => void exportAllSketches())}>
              Download all sketches
            </Item>
          )}
          {canInstall && (
            <Item value="Install as an app offline window" icon={<MonitorDown size={14} />} onSelect={() => run(() => void installApp())}>
              Install as an app
            </Item>
          )}
          <Item value="Use light dark theme toggle" icon={theme.theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />} onSelect={() => run(theme.toggle)}>
            Use {theme.theme === 'dark' ? 'light' : 'dark'} theme
          </Item>
          {activeRef && selectedEntity && modelInFront && (
            <Item value={`Focus on the selected entity ${selectedEntity}`} icon={<Crosshair size={14} />} onSelect={() => run(() => revealEntity(activeRef, selectedEntity, { focus: true }))}>
              Focus on {selectedEntity}
            </Item>
          )}
          {activeRef && selectedEntity && modelInFront && (
            <Item value={`What deleting the selected entity does cascade delete ${selectedEntity}`} icon={<CascadeIcon size={14} />} onSelect={() => run(() => revealEntity(activeRef, selectedEntity, { deletion: true }))}>
              What deleting a {selectedEntity} does
            </Item>
          )}
          {modelInFront && (
            <>
              <Item value="Show all properties" icon={<Rows3 size={14} />} onSelect={() => run(() => settings.setProperties('all'))}>
                Show all properties
              </Item>
              <Item value="Show only keys" icon={<Rows3 size={14} />} onSelect={() => run(() => settings.setProperties('keys'))}>
                Show only keys
              </Item>
              <Item value="Show only entity names" icon={<Rows3 size={14} />} onSelect={() => run(() => settings.setProperties('none'))}>
                Show only entity names
              </Item>
            </>
          )}
          {diagramInFront && (
            <Item value="Show hide the minimap toggle" icon={<Map size={14} />} onSelect={() => run(settings.toggleMinimap)}>
              {settings.minimap ? 'Hide' : 'Show'} the minimap
            </Item>
          )}
          <Item value="Close this folder" icon={<FolderX size={14} />} onSelect={() => run(() => { closeAllPanels(); closeSource(); })}>
            Close this folder
          </Item>
        </Group>
      </Command.List>
    </Command.Dialog>
  );
}
